import { useEffect, useState } from 'react';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { useFeatureToggles } from '../../../feature-toggles';
import { useAuthenticatedUser } from '../../../authentication';
import { useIsAllowed } from '../../../authentication/use-is-allowed';
import { ResourceOperations, ResourceTypes } from '../../../authentication/resources';
import { ISummaPreview } from '../../../hooks/ai-search/use-chat';
import { readPreviewModel, storePreviewModel } from '../summa-preview-storage';

export const modelPreviewToggle = 'AiSummaModelPreview';

// Looked up again on request, because Ops can withdraw the coming model at any time.
export function useSummaPreview() {
  const userId = useAuthenticatedUser()?.profile.id;
  const [offeredPreview, setOfferedPreview] = useState<ISummaPreview | undefined>(undefined);
  const [chosenModel, setChosenModel] = useState(() => readPreviewModel(userId));
  const { isFeatureEnabled } = useFeatureToggles();
  const mayTryPreview = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Write }]) && isFeatureEnabled(modelPreviewToggle);
  const chosenPreview = offeredPreview && offeredPreview.model === chosenModel ? offeredPreview : undefined;

  function choosePreview(nextPreview?: ISummaPreview) {
    setChosenModel(nextPreview?.model);
    storePreviewModel(userId, nextPreview?.model);
  }

  // A withdrawn model may come back and is still the one chosen, but a different coming model has to be chosen anew.
  function forgetChoiceOfOtherModel(offeredModel: string) {
    if (chosenModel && chosenModel !== offeredModel) {
      choosePreview(undefined);
    }
  }

  async function lookUpOfferedPreview() {
    if (!mayTryPreview) {
      setOfferedPreview(undefined);
      return;
    }

    // No error message for the user: a failed lookup only means there is nothing to try.
    try {
      const result = await moduleClientFactory.createAiSearchClient(false).getSummaPreviewModel();
      const offered = result.model && result.displayName ? { model: result.model, displayName: result.displayName } : undefined;
      setOfferedPreview(offered);
      if (offered) {
        forgetChoiceOfOtherModel(offered.model);
      }
    } catch {
      setOfferedPreview(undefined);
    }
  }

  useEffect(() => {
    lookUpOfferedPreview();
  }, [mayTryPreview]);

  return { mayTryPreview, offeredPreview, chosenPreview, choosePreview, lookUpOfferedPreview };
}
